import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { closeMenu } from "../../../../State/actions";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

const ButtonMenuClose = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device } = useDimensions();

  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const texture = getUITexture("icon_close");

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Right side positioning — same as ButtonFullscreen
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return {
        x: toolbar.width - buttonConfig.width * 1.0,
        y: toolbar.height / 2,
      };
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return {
        x: toolbar.width - buttonConfig.width * 1.0,
        y: toolbar.height / 2,
      };
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2,
      };
    }
  }, [device, toolbar.width, toolbar.height, buttonConfig.width]);

  const handleClick = () => {
    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();

    // Delay close so the press animation (80ms + 180ms) plays fully before the overlay fades out
    setTimeout(() => {
      dispatch(closeMenu());
    }, 260);

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Container
      name="button_menu_close"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_menu_close_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={t("close", { ns: "common" })}
          accessibleHint={t("close", { ns: "common" })}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonMenuClose;
